import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { hasModule } from '../../utils/plan.js';
import Modal from './Modal.jsx';
import Button from './Button.jsx';
import AiAssist from './AiAssist.jsx';

// Overdracht: hand a member's open work and functions to a successor. One successor by
// default; per item someone else or "niet overdragen", with a remark. With mode="deactivate"
// the member is removed from the club afterwards (a successor is optional then). A draft
// the member prepared on their profile is filled in. HandoverFields is shared with that draft.

const ERRORS = {
  invalid_successor: 'Kies een opvolger die actief lid is (niet de vertrekker zelf).',
  invalid_receiver: 'Een gekozen ontvanger is geen actief lid meer.',
  invalid_item: 'Er is intussen iets veranderd aan het werk. Laad de pagina opnieuw.',
  item_changed: 'Er is intussen iets veranderd aan het werk van deze persoon. Open de overdracht opnieuw.',
  calendar_group_name_taken: 'De opvolger heeft al een agendagroep met dezelfde naam. Hernoem een van beide en probeer opnieuw.',
  nothing_to_hand_over: 'Er is niets gekozen om over te dragen.',
  note_too_long: 'Een notitie is te lang (max. 4000 tekens).',
  remark_too_long: 'Een opmerking is te lang (max. 500 tekens).',
  invalid_note: 'Een notitie hoort bij een functie die niet (meer) bij deze persoon hoort. Laad de pagina opnieuw.',
  cannot_delete_self: 'Je kunt jezelf niet verwijderen.',
  meeting_role_taken: 'De ontvanger is in een gremium al voorzitter of secretaris. Kies voor dat gremium iemand anders.',
  functions_need_update: 'Functies overdragen mag alleen wie leden mag bewerken. Zet de functies op "Niet overdragen".',
};
export const errorText = err => ERRORS[err?.code] || 'Dat lukte niet.';
export const NONE = 'none';
export const itemKey = i => `${i.type}:${i.id}`;

// The form state from an inventory (with the member's draft, if any)
export function formFromInventory(data) {
  const notes = {};
  for (const i of data.items) {
    if (i.type === 'function' && i.category_id && notes[i.category_id] === undefined) {
      // Start from the latest note of each function, to update rather than retype
      notes[i.category_id] = data.notes.find(n => n.category_id === i.category_id)?.note || '';
    }
  }
  const d = data.draft;
  const overrides = {};
  const remarks = {};
  for (const [k, v] of Object.entries(d?.items || {})) {
    if (v.to_user_id === null) overrides[k] = NONE;
    else if (v.to_user_id) overrides[k] = String(v.to_user_id);
    if (v.remark) remarks[k] = v.remark;
  }
  return {
    successor: d?.to_user_id ? String(d.to_user_id) : '', overrides, remarks,
    notes: { ...notes, ...(d?.notes || {}) }, note: d?.note || '',
  };
}

export function receiverOf(form, i) {
  if (!form.successor) return null;
  const o = form.overrides[itemKey(i)];
  if (o === NONE) return null;
  return o ? Number(o) : Number(form.successor);
}

// alwaysOpen: show remarks and notes before a successor is chosen (the draft)
export function HandoverFields({ inv, members, form, setForm, ownerName, noSuccessorLabel, alwaysOpen = false }) {
  const set = patch => setForm(f => ({ ...f, ...patch }));
  const groups = [];
  for (const i of inv.items) {
    let g = groups.find(x => x.type === i.type);
    if (!g) groups.push(g = { type: i.type, label: i.type_label, items: [] });
    g.items.push(i);
  }
  const open = alwaysOpen || !!form.successor;
  const successorName = members.find(m => m.id === Number(form.successor))?.display_name || 'opvolger';
  return (
    <div style="display:flex;flex-direction:column;gap:16px">
      <div class="form-group" style="margin:0">
        <label class="form-label">Opvolger</label>
        <select class="form-select" value={form.successor} onChange={e => set({ successor: e.target.value })} aria-label="Opvolger">
          <option value="">{noSuccessorLabel}</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
        <div class="text-sm text-muted" style="margin-top:4px">
          Alles gaat standaard naar de opvolger. Per onderdeel kun je iemand anders kiezen of het niet overdragen.
        </div>
      </div>

      {inv.items.length === 0 && (
        <div style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);border-radius:8px;padding:12px 16px;font-size:13px;color:var(--color-text-muted)">
          {ownerName} heeft geen functies of openstaand werk.
        </div>
      )}

      {groups.map(g => (
        <div key={g.type}>
          <div style="font-size:13px;font-weight:600;margin-bottom:6px">{g.label} ({g.items.length})</div>
          <div style="display:flex;flex-direction:column;gap:6px">
            {g.items.map(i => (
              <div key={itemKey(i)} style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);border-radius:8px;padding:8px 12px">
                <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
                  <div style="flex:1;min-width:140px;font-size:13px">
                    <div style="font-weight:500;overflow-wrap:anywhere">{i.label}</div>
                    {i.detail && <div class="text-sm text-muted">{i.detail}</div>}
                  </div>
                  {form.successor && (
                    <select class="form-select" style="width:180px;max-width:100%;font-size:13px" value={form.overrides[itemKey(i)] || ''}
                      onChange={e => set({ overrides: { ...form.overrides, [itemKey(i)]: e.target.value } })} aria-label={`Ontvanger voor ${i.label}`}>
                      <option value="">→ {successorName}</option>
                      <option value={NONE}>Niet overdragen</option>
                      {members.filter(m => m.id !== Number(form.successor)).map(m => <option key={m.id} value={m.id}>→ {m.display_name}</option>)}
                    </select>
                  )}
                </div>
                {open && (
                  <input class="form-input" style="margin-top:6px;font-size:13px" maxLength={500} value={form.remarks[itemKey(i)] || ''}
                    onInput={e => set({ remarks: { ...form.remarks, [itemKey(i)]: e.target.value } })}
                    placeholder="Opmerking voor de ontvanger (optioneel)" aria-label={`Opmerking bij ${i.label}`} />
                )}
                {open && i.type === 'function' && i.category_id && (
                  <textarea class="form-input" rows={2} maxLength={4000} style="margin-top:6px;font-size:13px" value={form.notes[i.category_id] || ''}
                    onInput={e => set({ notes: { ...form.notes, [i.category_id]: e.target.value } })}
                    placeholder={`Overdrachtsnotitie ${i.label}: wat moet de opvolger weten?`} aria-label={`Overdrachtsnotitie ${i.label}`} />
                )}
                {open && i.type === 'function' && i.category_id && (
                  <AiAssist kind="handover_note" value={form.notes[i.category_id] || ''} context={{ function: i.label }}
                    onUse={text => set({ notes: { ...form.notes, [i.category_id]: text } })} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {open && (
        <div class="form-group" style="margin:0">
          <label class="form-label">Algemene notitie (optioneel)</label>
          <textarea class="form-input" rows={2} maxLength={4000} value={form.note} onInput={e => set({ note: e.target.value })} placeholder="Bijv. contactgegevens, afspraken, waar dingen liggen…" />
          <AiAssist kind="handover_note" value={form.note} onUse={text => set({ note: text })} />
        </div>
      )}
    </div>
  );
}

export const fmtDate = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' }) : '');
const EMPTY = { successor: '', overrides: {}, remarks: {}, notes: {}, note: '' };

export default function HandoverModal({ isOpen, user, mode = 'handover', onClose, onDone }) {
  const deactivate = mode === 'deactivate';
  // Without Overdracht in the plan, removing a member is just removing (nothing to hand over)
  const withHandover = hasModule('handovers');
  const [inv, setInv] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Set once the handover went through, so a retry after a failed delete only deletes
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!isOpen || !user) return;
    setInv(null); setForm(EMPTY); setError(null); setLoadFailed(false); setDone(null);
    if (!withHandover) { setInv({ items: [], notes: [] }); setMembers([]); return; }
    Promise.all([api(`/api/handovers/inventory/${user.id}`), api('/api/users/members')])
      .then(([data, list]) => {
        setInv(data);
        setMembers(list.filter(m => m.id !== user.id));
        setForm(formFromInventory(data));
      })
      .catch(() => { setInv({ items: [], notes: [] }); setLoadFailed(true); setError('Het werk van deze persoon kon niet worden geladen. Sluit en probeer opnieuw.'); });
  }, [isOpen, user?.id]);

  const items = inv?.items || [];
  const moving = items.filter(i => receiverOf(form, i) !== null).length;
  // Only notes that differ from the latest one are saved again
  const changedNotes = Object.fromEntries(Object.entries(form.notes).filter(([cat, text]) =>
    text.trim() && text.trim() !== (inv?.notes.find(n => String(n.category_id) === cat)?.note || '')));
  const hasHandover = form.successor && (moving > 0 || Object.keys(changedNotes).length > 0 || form.note.trim());

  async function submit() {
    setBusy(true); setError(null);
    let result = done;
    try {
      if (hasHandover && !result) {
        result = await api('/api/handovers', {
          method: 'POST',
          body: JSON.stringify({
            from_user_id: user.id, to_user_id: Number(form.successor), note: form.note,
            items: items.map(i => ({ type: i.type, id: i.id, to_user_id: receiverOf(form, i), remark: form.remarks[itemKey(i)] || null })),
            notes: changedNotes,
          }),
        });
        setDone(result);
      }
      if (deactivate) await api(`/api/users/${user.id}`, { method: 'DELETE', body: JSON.stringify({}) });
      onDone?.(result);
    } catch (err) {
      setError(result ? `De overdracht is gelukt, maar verwijderen niet: ${errorText(err)}. Probeer opnieuw om alleen te verwijderen.` : errorText(err));
    }
    setBusy(false);
  }

  const first = (user?.display_name || '').split(' ')[0];
  return (
    <Modal isOpen={isOpen} title={`${deactivate ? (withHandover ? 'Verwijderen en overdragen' : 'Verwijderen') : 'Overdracht'} — ${user?.display_name || ''}`} onClose={() => !busy && onClose()}>
      {!inv ? (
        <p style="color:var(--color-text-muted);padding:24px 0;text-align:center">Gegevens worden geladen…</p>
      ) : (
        <div style="display:flex;flex-direction:column;gap:16px">
          {inv.draft && (
            <div class="alert" style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);font-size:13px;margin:0">
              📝 {first} heeft deze overdracht zelf voorbereid{inv.draft.submitted_at ? ` (klaargezet op ${fmtDate(inv.draft.submitted_at)})` : ' (nog niet klaargezet)'}. Controleer en voer uit.
            </div>
          )}
          {withHandover && <HandoverFields inv={inv} members={members} form={form} setForm={setForm} ownerName={first}
            noSuccessorLabel={deactivate ? '— Geen opvolger —' : 'Kies een opvolger'} />}
          <p style="font-size:12px;color:var(--color-text-muted);margin:0;line-height:1.6">
            {!withHandover ? null : form.successor
              ? `${moving} onderde${moving === 1 ? 'el gaat' : 'len gaan'} over. De ontvangers krijgen een melding en een overzicht om te bevestigen; wie met het werk te maken heeft, hoort wie het nieuwe aanspreekpunt is.`
              : 'Kies een opvolger om werk over te dragen.'}
            {deactivate && <> <strong>{user?.display_name}</strong> verliest daarna direct toegang. Taken en processen {withHandover ? 'die niet worden overgedragen ' : ''}komen zonder eigenaar te staan; functies, gremiumplekken en de rest worden losgekoppeld (agendagroepen blijven op naam van {first}).</>}
          </p>
          {error && <div class="alert alert-error">{error}</div>}
          <div class="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Annuleren</Button>
            <Button variant={deactivate ? 'danger' : 'primary'} onClick={submit} disabled={busy || loadFailed || (!deactivate && !hasHandover)}>
              {busy ? 'Bezig…' : deactivate ? (hasHandover && !done ? 'Overdragen en verwijderen' : 'Verwijderen') : 'Overdragen'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
